import React from 'react';
import styled from 'styled-components';
import breakpoints from '@/constants/breakpoints';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import Skeleton from 'react-loading-skeleton';

export default function Instagram() {
  const { loading: imagesLoading, data, error } = useQuery(INSTAGRAM_POSTS, {
    notifyOnNetworkStatusChange: true,
    variables: {
      first: 4,
      page: 1
    }
  });

  const instagramLink = 'https://www.instagram.com/theinside';

  if (error) {
    console.log(error);
    return `Sorry there was an error, please try again or contact us ${error}`;
  }

  return (
    <Container>
      <InstagramLink rel="noopener" href={instagramLink} target="_blank">
        @theinside
      </InstagramLink>
      <Title>The Inside, styled by you.</Title>
      <div className="ig-images-container">
        {imagesLoading && !data
          ? renderInstagramSkeleton()
          : renderInstagramPosts(data, instagramLink)}
      </div>
    </Container>
  );
}

const renderInstagramPosts = (data, instagramLink) => {
  return data.instagramPosts.data.map(post => {
    return (
      <div key={post.caption}>
        <ImageContainer className="image-container">
          <a href={instagramLink} target="_blank" rel="noopener">
            {post.media_url && (
              <img
                loading="lazy"
                className="ig-post-image"
                src={post.media_url}
                alt={post.caption}
              />
            )}
          </a>
        </ImageContainer>
      </div>
    );
  });
};

const renderInstagramSkeleton = () => {
  return Array.from(Array(4).keys()).map((_, i) => {
    return (
      <div key={i}>
        <ImageContainer className="image-container">
          <a href="https://www.instagram.com/theinside/" target="_blank">
            <Skeleton />
          </a>
        </ImageContainer>
      </div>
    );
  });
};

const Container = styled.div`
  text-align: center;
  padding: 60px 0 0;

  @media (min-width: ${breakpoints['tablet']}px) {
    padding: 67px 0 0;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    padding: 72px 0 0;
  }

  .ig-images-container {
    display: grid;
    margin: 0 auto 0;
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px;
    max-width: 343px;

    @media (min-width: ${breakpoints['tablet']}px) {
      grid-template-columns: 1fr 1fr 1fr 1fr;
      max-width: 724px;
    }

    @media (min-width: ${breakpoints['desktop']}px) {
      grid-gap: 20px;
      max-width: 1000px;
    }

    @media (min-width: ${breakpoints['large-desktop']}px) {
      max-width: 1350px;
    }
  }
`;

const InstagramLink = styled.a`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 16px;
  line-height: 1;
  color: ${props => props.theme.colors.bluelink};

  @media (min-width: ${breakpoints['desktop']}px) {
    font-size: 18px;
    line-height: 1.11;
    letter-spacing: -0.36px;
  }

  @media (min-width: ${breakpoints['large-desktop']}px) {
    font-size: 20px;
    line-height: 1;
    letter-spacing: -0.4px;
  }
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.fonts.secondary.family};
  letter-spacing: ${({ theme }) => theme.old ? '-0.52px' : theme.fonts.secondary.letterSpacing};
  font-size: 26px;
  font-weight: 300;
  line-height: 1.23;
  text-align: center;
  color: ${props => props.theme.colors.charcoal};
  margin-top: 15px;
  margin-bottom: 31px;

  @media (min-width: ${breakpoints['tablet']}px) {
    font-size: 28px;
    line-height: 1.43;
    letter-spacing: -0.56px;
    margin-top: 15px;
    margin-bottom: 26px;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    font-size: 40px;
    line-height: 1;
    letter-spacing: -0.8px;
    margin-bottom: 47px;
  }

  @media (min-width: ${breakpoints['large-desktop']}px) {
    font-size: 56px;
    line-height: 1.34;
    letter-spacing: -1.68px;
    margin-bottom: 53px;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  max-width: 343px;
  height: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding-bottom: 100%;

  a {
    position: absolute !important;
    top: 0;
    left: 0;
    background: #f7f9fa;
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    .ig-post-image {
      position: absolute;
      max-width: 100%;
      max-height: 100%;
      position: absolute;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`;

const INSTAGRAM_POSTS = gql`
  query instagramPosts($first: Int!, $page: Int!) {
    instagramPosts(first: $first, page: $page) {
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
      data {
        id
        caption
        media_url
      }
    }
  }
`;
